import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Loader2, Lock, ShieldAlert, TrendingDown, TrendingUp } from 'lucide-react';

export const Panel = ({ title, eyebrow, action, children, className = '', testid, bodyClass = 'px-5 pb-5' }) => (
  <section data-testid={testid} className={`panel fade-up ${className}`}>
    {(title || action) && (
      <div className="panel-head">
        <div>
          {eyebrow && <div className="eyebrow mb-0.5">{eyebrow}</div>}
          {title && <h3 className="panel-title">{title}</h3>}
        </div>
        {action}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

export const Kpi = ({ label, value, sub, delta, tone = 'teal', icon: Icon, testid, onClick }) => {
  const tones = { teal: 'text-[#00A9CE]', yellow: 'text-[#FFCD00]', red: 'text-red-400', green: 'text-emerald-400', orange: 'text-orange-400' };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`panel fade-up text-left px-4 py-3.5 transition-colors ${onClick ? 'hover:border-[#00A9CE]/50 cursor-pointer' : 'cursor-default'}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${tones[tone]}`} />}
      </div>
      <div className="mt-2 font-mono text-[26px] font-bold leading-none tracking-tight text-white" data-testid={testid && `${testid}-value`}>{value}</div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
        {delta != null && (
          <span className={`inline-flex items-center gap-0.5 ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(delta)}%
          </span>
        )}
        <span className="truncate">{sub}</span>
      </div>
    </button>
  );
};

const PILL = {
  healthy: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30', watchlist: 'bg-amber-500/12 text-amber-300 border-amber-500/30',
  distressed: 'bg-red-500/14 text-red-300 border-red-500/40', Operating: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30',
  Idle: 'bg-slate-500/15 text-slate-300 border-slate-500/30', Down: 'bg-red-500/14 text-red-300 border-red-500/40',
  'In Service': 'bg-blue-500/12 text-blue-300 border-blue-500/30', 'Awaiting Delivery': 'bg-cyan-500/12 text-cyan-300 border-cyan-500/30',
  critical: 'bg-red-500/20 text-red-200 border-red-500/50', high: 'bg-orange-500/14 text-orange-300 border-orange-500/40',
  medium: 'bg-amber-500/12 text-amber-300 border-amber-500/30', low: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  open: 'bg-orange-500/14 text-orange-300 border-orange-500/30', in_progress: 'bg-blue-500/12 text-blue-300 border-blue-500/30',
  resolved: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30', awaiting_sme: 'bg-amber-500/12 text-amber-300 border-amber-500/30',
  escalated: 'bg-red-500/14 text-red-300 border-red-500/40', draft: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  submitted: 'bg-cyan-500/12 text-cyan-300 border-cyan-500/30', under_review: 'bg-blue-500/12 text-blue-300 border-blue-500/30',
  info_requested: 'bg-amber-500/12 text-amber-300 border-amber-500/30', approved_simulated: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30',
  declined_simulated: 'bg-red-500/14 text-red-300 border-red-500/40', paid: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30',
  scheduled: 'bg-slate-500/15 text-slate-300 border-slate-500/30', overdue: 'bg-red-500/14 text-red-300 border-red-500/40',
  flagged: 'bg-orange-500/14 text-orange-300 border-orange-500/40', received: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30',
  outstanding: 'bg-slate-500/15 text-slate-300 border-slate-500/30', delayed: 'bg-orange-500/14 text-orange-300 border-orange-500/40',
  provided: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30', missing: 'bg-red-500/14 text-red-300 border-red-500/40',
  requested: 'bg-amber-500/12 text-amber-300 border-amber-500/30', operational: 'bg-blue-500/12 text-blue-300 border-blue-500/30',
  business: 'bg-amber-500/12 text-amber-300 border-amber-500/30', finance: 'bg-fuchsia-500/12 text-fuchsia-300 border-fuchsia-500/30',
  linked: 'bg-cyan-500/12 text-cyan-300 border-cyan-500/30', closed: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30',
  active: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30', pending_activation: 'bg-cyan-500/12 text-cyan-300 border-cyan-500/30',
  ready_for_decision: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/30', gaps_identified: 'bg-amber-500/12 text-amber-300 border-amber-500/30',
  not_ready: 'bg-red-500/14 text-red-300 border-red-500/40', not_assessed: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};
export const Pill = ({ v, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${PILL[v] || PILL.low}`}>
    {label || (v ? v.replace(/_simulated/, ' (simulated)').replace(/_/g, ' ') : '—')}
  </span>
);

export const Crumbs = ({ items }) => (
  <nav className="flex flex-wrap items-center gap-1 text-xs text-slate-400" data-testid="breadcrumbs">
    {items.map((it, i) => (
      <span key={i} className="flex items-center gap-1">
        {i > 0 && <ChevronRight className="h-3 w-3" />}
        {it.to ? <Link to={it.to} className="hover:text-[#FFCD00]">{it.label}</Link> : <span className="text-slate-200">{it.label}</span>}
      </span>
    ))}
  </nav>
);

export const PageHeader = ({ code, stage, title, subtitle, crumbs, actions }) => (
  <div className="fade-up flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
    <div>
      {crumbs && <Crumbs items={crumbs} />}
      <div className="mt-1.5 flex items-center gap-2">
        {code && <span className="rounded bg-[#FFCD00]/12 px-1.5 py-0.5 font-mono text-[10px] font-bold text-[#FFCD00]">{code}</span>}
        {stage && <span className="rounded border border-[#00A9CE]/40 px-1.5 py-0.5 font-mono text-[10px] tracking-widest text-[#22D3EE]">{stage}</span>}
      </div>
      <h1 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 max-w-3xl text-sm text-slate-400">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Loading = ({ label = 'Loading synthetic records…' }) => (
  <div className="flex items-center gap-2 p-10 text-sm text-slate-400" data-testid="loading-state"><Loader2 className="h-4 w-4 animate-spin text-[#00A9CE]" />{label}</div>
);

export const ErrorState = ({ error, status }) => (
  <div className="panel m-1 flex items-start gap-3 p-6" data-testid="error-state">
    {status === 403 ? <Lock className="h-5 w-5 text-[#FFCD00]" /> : <AlertTriangle className="h-5 w-5 text-red-400" />}
    <div>
      <div className="font-display font-semibold text-white">{status === 403 ? 'Access restricted for this role' : 'Unable to load'}</div>
      <p className="mt-1 text-sm text-slate-400">{error}</p>
    </div>
  </div>
);

export const Empty = ({ text = 'No records match the current filters.' }) => (
  <div className="py-8 text-center text-sm text-slate-500" data-testid="empty-state">{text}</div>
);

export const Restricted = () => (
  <div className="panel flex items-start gap-3 p-8" data-testid="restricted-state">
    <ShieldAlert className="h-6 w-6 text-[#FFCD00]" />
    <div>
      <div className="font-display text-lg font-semibold text-white">This module is not available to the selected role</div>
      <p className="mt-1 text-sm text-slate-400">Permissions are enforced by the backend. Switch stakeholder role from the top bar to access this area.</p>
    </div>
  </div>
);

export const Disclosure = ({ text = 'ILLUSTRATIVE DEMONSTRATION — NOT AN ACTUAL LENDING DECISION', testid = 'decision-disclosure' }) => (
  <div data-testid={testid} className="flex items-center gap-2 rounded-md border border-[#FFCD00]/50 bg-[#FFCD00]/10 px-4 py-2.5 font-mono text-xs font-bold tracking-wider text-[#FFCD00]">
    <ShieldAlert className="h-4 w-4 shrink-0" />{text}
  </div>
);

export const Gauge = ({ value, label, size = 76, color }) => {
  const r = size / 2 - 7;
  const c = 2 * Math.PI * r;
  const col = color || (value >= 80 ? '#10B981' : value >= 60 ? '#FFCD00' : '#EF4444');
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1E2A45" strokeWidth="6" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={col} strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ transition: 'stroke-dashoffset .8s ease' }} />
        <text x={size / 2} y={size / 2} dominantBaseline="central" textAnchor="middle" transform={`rotate(90 ${size / 2} ${size / 2})`}
          fill="#FFFFFF" fontSize={size > 90 ? 20 : 13} fontWeight="700" fontFamily="JetBrains Mono, monospace">{`${value}%`}</text>
      </svg>
      <span className="text-[11px] text-slate-400">{label}</span>
    </div>
  );
};

export const KV = ({ k, v, testid }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-[#1E2A45]/70 py-1.5 text-sm last:border-0">
    <span className="text-slate-400">{k}</span>
    <span className="text-right font-medium text-slate-100" data-testid={testid}>{v}</span>
  </div>
);

export const SourceTag = ({ s }) => (
  <span className="inline-flex items-center gap-1 rounded border border-[#2B3B60] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-400">
    <span className="h-1.5 w-1.5 rounded-full bg-[#00A9CE]" />{s}
  </span>
);

export const Field = ({ label, children, error, hint }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-300">{label}</span>
    {children}
    {hint && !error && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    {error && <span className="mt-1 block text-[11px] text-red-400">{error}</span>}
  </label>
);
